import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Form, Button, Alert, Spinner, Row, Col } from 'react-bootstrap';
import { FiArrowLeft, FiUpload, FiCheckCircle } from 'react-icons/fi';
import { importActivityPoliciesGreenRiverCruises } from '../../services/api';
import { handleApiError } from '../../utils/errorHandler';
import 'bootstrap/dist/css/bootstrap.css';
import './AdminPolicies.css';

const IMPORT_TYPE_GREEN_RIVER = 'green-river-cruises';

const AdminActivityPoliciesImport = () => {
    const navigate = useNavigate();
    const [importFile, setImportFile] = useState(null);
    const [importType, setImportType] = useState('');
    const [importLoading, setImportLoading] = useState(false);
    const [importError, setImportError] = useState(null);
    const [importSuccess, setImportSuccess] = useState(() => {
        const savedMessage = localStorage.getItem('importActivityPoliciesMessage');
        return savedMessage || null;
    });

    const handleImport = async () => {
        if (!importType || importType.trim() === '') {
            setImportError('Veuillez sélectionner un type d\'import');
            return;
        }
        if (!importFile) {
            setImportError('Veuillez sélectionner un fichier Excel');
            return;
        }

        setImportLoading(true);
        setImportError(null);
        setImportSuccess(null);

        try {
            if (importType !== IMPORT_TYPE_GREEN_RIVER) {
                throw new Error('Type d\'import invalide');
            }

            const response = await importActivityPoliciesGreenRiverCruises(importFile);
            let successMessage = 'Import Green River Cruises terminé.\n\n';

            if (response?.message) {
                successMessage += `ℹ️ ${response.message}\n`;
            }
            if (typeof response?.created_policies === 'number') {
                successMessage += `✅ Polices créées : ${response.created_policies}\n`;
            } else if (response?.success_count !== undefined) {
                successMessage += `✅ Polices créées avec succès : ${response.success_count}\n`;
            } else if (Array.isArray(response?.created_policies) && response.created_policies.length > 0) {
                successMessage += `✅ Polices créées : ${response.created_policies.length}\n`;
                successMessage += `\n📋 Détail : ${response.created_policies.join(', ')}\n`;
            }
            if (response?.error_count !== undefined) {
                successMessage += `❌ Erreurs : ${response.error_count}\n`;
            } else if (response?.errors && Array.isArray(response.errors) && response.errors.length > 0) {
                successMessage += `❌ Erreurs : ${response.errors.length}\n`;
            }
            if (response?.skipped_reservations && Array.isArray(response.skipped_reservations) && response.skipped_reservations.length > 0) {
                successMessage += `⏭️ Réservations ignorées : ${response.skipped_reservations.length}\n`;
            }
            if (Array.isArray(response?.policies) && response.policies.length > 0) {
                const policyLabels = response.policies.map((policy, index) => {
                    if (typeof policy === 'string') return policy;
                    return policy.policy_code || policy.reservation_number || `Police ${index + 1}`;
                });
                successMessage += `\n📋 Polices : ${policyLabels.join(', ')}\n`;
            }
            if (response?.errors && Array.isArray(response.errors) && response.errors.length > 0) {
                successMessage += '\n⚠️ Détails des erreurs :\n';
                response.errors.forEach((error, index) => {
                    const reservationNumber = error.reservation_number || error.reservationNumber || 'N/A';
                    const rowPart = error.row != null ? `Ligne ${error.row} — ` : '';
                    const errorMsg = error.error || error.message || 'Erreur inconnue';
                    successMessage += `${index + 1}. ${rowPart}Réservation ${reservationNumber}: ${errorMsg}\n`;
                });
            }
            if (response?.skipped_reservations && Array.isArray(response.skipped_reservations) && response.skipped_reservations.length > 0) {
                successMessage += `\n⏭️ Réservations ignorées (${response.skipped_reservations.length}) :\n`;
                response.skipped_reservations.forEach((skipped, index) => {
                    const reservationNumber = skipped.reservation_number || skipped.reservationNumber || 'N/A';
                    const rowPart = skipped.row != null ? `Ligne ${skipped.row} — ` : '';
                    const skipMsg = skipped.message || 'Réservation ignorée';
                    successMessage += `${index + 1}. ${rowPart}Réservation ${reservationNumber}: ${skipMsg}\n`;
                });
            }

            setImportSuccess(successMessage);
            localStorage.setItem('importActivityPoliciesMessage', successMessage);

            setTimeout(() => {
                setImportFile(null);
                setImportType('');
            }, 5000);
        } catch (error) {
            setImportError(handleApiError(error, 'Erreur lors de l\'import du fichier'));
        } finally {
            setImportLoading(false);
        }
    };

    const handleReset = () => {
        setImportFile(null);
        setImportType('');
        setImportError(null);
        setImportSuccess(null);
        localStorage.removeItem('importActivityPoliciesMessage');
    };

    const handleClearMessage = () => {
        setImportSuccess(null);
        localStorage.removeItem('importActivityPoliciesMessage');
    };

    return (
        <div className="dashboard-page admin-policies-page">
            <Card>
                <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <div>
                            <h2 className="page-title">
                                <FiUpload className="me-2" />
                                Importer des polices Activity
                            </h2>
                            <p className="text-muted mb-0">Importez des polices depuis un fichier Excel</p>
                        </div>
                        <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                            <FiArrowLeft className="me-2" /> Retour
                        </Button>
                    </div>

                    {importLoading && (
                        <Alert variant="info" className="mb-4">
                            <Spinner size="sm" className="me-2" animation="border" />
                            <strong>Import en cours...</strong> Cela peut prendre plusieurs minutes.
                        </Alert>
                    )}

                    <Card className="mb-4">
                        <Card.Body>
                            <Form>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-4">
                                            <Form.Label className="fw-bold">
                                                Type d'import <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Select
                                                value={importType}
                                                onChange={(e) => {
                                                    setImportType(e.target.value);
                                                    setImportError(null);
                                                }}
                                                disabled={importLoading}
                                                required
                                            >
                                                <option value="">Sélectionner un type d'import</option>
                                                <option value={IMPORT_TYPE_GREEN_RIVER}>Green River Cruises</option>
                                            </Form.Select>
                                            <Form.Text className="text-muted">
                                                Sélectionnez le type de politiques à importer
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Form.Group className="mb-4">
                                    <Form.Label className="fw-bold">
                                        Fichier Excel <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="file"
                                        accept=".xlsx,.xls,.csv"
                                        onChange={(e) => {
                                            const file = e.target.files[0];
                                            if (file) {
                                                setImportFile(file);
                                                setImportError(null);
                                            }
                                        }}
                                        disabled={importLoading}
                                    />
                                    <Form.Text className="text-muted d-block mt-2">
                                        Formats acceptés: <strong>.xlsx</strong>, <strong>.xls</strong>, <strong>.csv</strong>
                                    </Form.Text>
                                    {importFile && (
                                        <div className="mt-3 p-3 rounded" style={{ backgroundColor: '#d4edda', border: '1px solid #c3e6cb' }}>
                                            <small className="text-success">
                                                <FiCheckCircle className="me-2" />
                                                <strong>Fichier sélectionné :</strong> {importFile.name}
                                            </small>
                                        </div>
                                    )}
                                </Form.Group>

                                <div className="d-flex gap-2 justify-content-end">
                                    <Button variant="outline-secondary" onClick={handleReset} disabled={importLoading}>
                                        Réinitialiser
                                    </Button>
                                    <Button
                                        variant="success"
                                        onClick={handleImport}
                                        disabled={importLoading || !importType || !importFile}
                                    >
                                        {importLoading ? (
                                            <>
                                                <Spinner size="sm" className="me-2" animation="border" />
                                                Import en cours...
                                            </>
                                        ) : (
                                            <>
                                                <FiUpload className="me-2" />
                                                Importer
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>

                    {importError && (
                        <Alert variant="danger" className="mb-4" dismissible onClose={() => setImportError(null)}>
                            <strong>Erreur :</strong> {importError}
                        </Alert>
                    )}

                    {importSuccess && (
                        <Alert
                            variant={
                                importSuccess.includes('❌')
                                || importSuccess.includes('Erreurs :')
                                || importSuccess.includes('Réservations ignorées')
                                    ? 'warning'
                                    : 'success'
                            }
                            className="mb-4"
                            dismissible
                            onClose={handleClearMessage}
                            style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                        >
                            <strong>Résultat de l'import :</strong>
                            <pre style={{
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                marginTop: '10px',
                                marginBottom: 0,
                                fontSize: '0.9rem',
                                backgroundColor: 'rgba(255, 255, 255, 0.3)',
                                padding: '10px',
                                borderRadius: '4px',
                                fontFamily: 'inherit',
                                maxHeight: '400px',
                                overflowY: 'auto'
                            }}>{importSuccess}</pre>
                        </Alert>
                    )}
                </Card.Body>
            </Card>
        </div>
    );
};

export default AdminActivityPoliciesImport;
